/** Directive to force a menu trigger component to contain a `isSelected` signal
 * so we can update trigger state from outside (menu)
 *
 * You must implement this directive on your trigger component
 * ```ts
 * @Component({
 *   selector: 'evc-nav-button',
 *   providers: [{
 *     provide: HAS_SELECTED_SIGNAL,
 *     useExisting: NavButtonComponent,
 *   }],
 * })
 * export class NavButtonComponent implements HasSelectedSignal {
 *   isSelected = signal(false);
 * }
 * ```
 *
 * And must declare this directive when using menu
 * ```html
 *<evc-menu class="menu" [menu]="help()">
 *  <!-- 👇 provide `trigger` slot + `evcMenuTriggerState` directive -->
 *  <evc-nav-button trigger evcMenuTriggerState
 *    class="button button--help"
 *    type="icon" size="md" variant="semi">
 *    <evc-icon size="sm" icon="help" />
 *  </evc-nav-button>
 *</evc-menu>
 * ```
 */
import { Directive, ElementRef, inject, InjectionToken, signal } from '@angular/core';

export type HasSelectedSignal = {
  selectedState: ReturnType<typeof signal>;
};

export const SELECTED_STATE_INJECTION_TOKEN = new InjectionToken<HasSelectedSignal>(
  'selectedStateSignal',
);

@Directive({
  selector: '[evcMenuTriggerState]',
})
export class MenuTriggerStateDirective {
  private el = inject(ElementRef);
  private host = inject<HasSelectedSignal>(SELECTED_STATE_INJECTION_TOKEN, {
    self: true,
    optional: true,
  });

  setSelected(val: boolean): void {
    if (this.host) {
      this.host.selectedState?.set(val);
    } else {
      this.el.nativeElement.dataset['active'] = val;
      this.el.nativeElement.setAttribute('aria-selected', val);
    }
  }
}
